import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { StressCheck, Healthcare } from 'jbc-front/components/icons';
import Button from 'jbc-front/components/Button';
import { Modal } from 'jbc-front/components/presenters/ui/Modal';
import { Checkbox } from 'jbc-front/components/presenters/form/Checkbox';
import { Label } from 'jbc-front/components/presenters/form/Label';
import { Select } from 'jbc-front/components/presenters/form/Select';
import { ErrorMessage } from 'jbc-front/components/presenters/form/ErrorMessage';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import { Features } from '../../query';
import { ACTIVATE_FEATURES, ActivateFeaturesQueryVariables } from './query';
import { Schema, schema } from './schema';
import styles from './ActivateModal.scss';

const stressCheckRollTypes = [
  { label: '実施者（全従業員の個人結果を確認できる方、産業医等）', value: 'practitioner' },
  {
    label: '実務担当者（労務担当者等）',
    value: 'person_in_charge'
  }
];

interface ActivateModalProps {
  isOpen: boolean;
  onClose: () => void;
  features: Features;
}

export const ActivateModal: FC<ActivateModalProps> = ({ isOpen, onClose, features }) => {
  const notify = useNotify();

  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = useForm<Schema>({
    resolver: yupResolver(schema),
    defaultValues: {
      stressCheckRole: null
    }
  });

  const stressCheck = watch('stressCheck');
  const regularHealthCheckup = watch('regularHealthCheckup');

  const [activateFeatures] = useMutation<unknown, ActivateFeaturesQueryVariables>(ACTIVATE_FEATURES);

  const onSubmit: SubmitHandler<Schema> = async data => {
    await activateFeatures({
      variables: {
        input: data
      }
    });

    notify('利用開始手続きが完了しました', 'success');
    window.location.reload();
  };

  return (
    <Modal className={styles.modal} isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>ジョブカン健康管理を利用する</Modal.Header>
      <Modal.Body className={styles.body}>
        <div className={styles.description}>
          健康管理機能の利用を開始します。
          <br />
          利用する機能にチェックを入れて「利用開始する」ボタンを押してください。
        </div>
        <ul className={styles.notice}>
          <li>・追加料金は発生いたしません</li>
          <li>・二人目以降の管理者は健康管理画面内の権限管理画面から追加できます</li>
        </ul>
        {!features.stressCheck && (
          <div className={styles.feature}>
            <h4 className={styles.featureTitle}>
              <StressCheck className={styles.featureIcon} size={20} />
              ストレスチェック
            </h4>
            <Checkbox {...register('stressCheck')}>利用する</Checkbox>
            <div className={styles.stressCheckRole}>
              <Label>あなたの役割</Label>
              <Controller
                control={control}
                name="stressCheckRole"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    className={styles.select}
                    options={stressCheckRollTypes}
                    onChange={({ value }) => onChange(value)}
                    onBlur={onBlur}
                    value={stressCheckRollTypes.find(option => option.value === value)}
                    disabled={!stressCheck}
                    placeholder="選択してください"
                  />
                )}
              />
              {errors.stressCheckRole && <ErrorMessage>{errors.stressCheckRole.message}</ErrorMessage>}
              <ul className={styles.stressCheckNotice}>
                <li>・「利用する」にチェックを入れると役割を選択できます。</li>
                <li>
                  ・ストレスチェックの結果等の閲覧権限がある方のみ、自身の役割を選択してストレスチェック機能の利用を開始してください。
                </li>
                <li>・ストレスチェックは法律上閲覧できる管理者が制限されています。</li>
              </ul>
            </div>
          </div>
        )}
        {!features.regularHealthCheckup && (
          <div className={styles.feature}>
            <h4 className={styles.featureTitle}>
              <Healthcare className={styles.featureIcon} size={20} />
              定期健康診断
            </h4>
            <Checkbox {...register('regularHealthCheckup')}>利用する</Checkbox>
          </div>
        )}
        <div className={styles.buttons}>
          <Button
            primary
            widthWide
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || (!stressCheck && !regularHealthCheckup)}>
            利用開始する
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
